/**
 * =============================================================================
 * Página: /afiliado/aguardando-aprovacao
 *         /afiliado/:tenantSlug/aguardando-aprovacao
 * Versão: 2.0.0 (Branding por tenant)
 *
 * Mudanças v2.0.0:
 *   - Aplica identidade visual personalizada do tenant (logo, cores, hero,
 *     bullets) usando `useAffiliateBrandingPublic` + `buildBrandingCssVars`,
 *     no mesmo padrão de AffiliateLogin / AffiliateSignup.
 *   - Layout em duas colunas (hero + card) preservando a mesma estética
 *     premium das demais páginas de auth do afiliado.
 *
 * Histórico:
 *   v1.0.0 — Card simples, sem branding.
 * =============================================================================
 */
import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Clock,
  LogOut,
  RefreshCw,
  Sparkles,
  TrendingUp,
  Wallet,
  Link2,
  Check,
  type LucideIcon,
} from 'lucide-react';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import {
  useAffiliateBrandingPublic,
  buildBrandingCssVars,
  type AffiliateBrandingLoginBullet,
} from '@/hooks/useAffiliateBranding';
import { affiliateLoginPath, useAffiliateTenantSlug } from '@/lib/affiliate-urls';
import { Button } from '@/components/ui/button';

// Defaults para os bullets do hero quando o tenant não personalizou
const DEFAULT_BULLETS: AffiliateBrandingLoginBullet[] = [
  { icon: 'link', text: 'Links únicos por campanha, prontos para divulgar' },
  { icon: 'chart', text: 'Funil de indicações em tempo real' },
  { icon: 'wallet', text: 'Saques rápidos via PIX' },
];

// Mapeamento string → componente lucide (mesmo padrão das outras páginas)
const ICON_MAP: Record<AffiliateBrandingLoginBullet['icon'], LucideIcon> = {
  link: Link2,
  chart: TrendingUp,
  wallet: Wallet,
  check: Check,
  sparkles: Sparkles,
};

export default function AffiliateAwaitingApproval() {
  const { affiliate, signOut, refresh } = useAffiliateAuth();

  // Resolve o tenantSlug efetivo (rota canônica, ?tenant=, cache local)
  const tenantSlug = useAffiliateTenantSlug();
  const { tenantSlug: tenantSlugParam } = useParams<{ tenantSlug?: string }>();
  const [searchParams] = useSearchParams();
  const tenantParam = searchParams.get('tenant');
  const programaParam = searchParams.get('programa');

  // Branding público — sem necessidade de auth
  const { data: brandingData } = useAffiliateBrandingPublic({
    tenantId: tenantParam,
    programSlug: programaParam,
    tenantSlug: tenantSlugParam ?? tenantSlug ?? null,
  });

  const brandingStyle = useMemo(
    () => buildBrandingCssVars(brandingData?.branding),
    [brandingData],
  );
  const logoUrl = brandingData?.branding?.logo_url ?? null;
  const tenantName = brandingData?.tenant_name ?? null;
  const bullets =
    brandingData?.login?.bullets && brandingData.login.bullets.length > 0
      ? brandingData.login.bullets
      : DEFAULT_BULLETS;

  const loginPath = affiliateLoginPath(tenantSlug);
  const firstName = affiliate?.full_name?.split(' ')[0] ?? null;

  return (
    <div
      className="affiliate-shell min-h-[100dvh] grid md:grid-cols-2 bg-[hsl(var(--aff-bg))]"
      style={brandingStyle}
    >
      {/* HERO — Desktop */}
      <aside className="aff-hero-surface relative hidden md:flex flex-col justify-between overflow-hidden p-10 lg:p-14">
        <div className="relative z-10 text-[hsl(var(--aff-primary-foreground))]">
          {logoUrl ? (
            <div className="flex flex-col gap-1.5">
              <img
                src={logoUrl}
                alt={tenantName ?? ''}
                className="max-h-14 lg:max-h-16 max-w-[220px] object-contain object-left"
              />
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] opacity-80">
                Painel do Afiliado
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm">
                <Sparkles className="h-5 w-5" />
              </div>
              <span className="text-sm font-semibold tracking-wide opacity-90">
                {tenantName ? `${tenantName} · Painel do Afiliado` : 'Painel do Afiliado'}
              </span>
            </div>
          )}
        </div>

        <div className="relative z-10 max-w-md space-y-7 text-[hsl(var(--aff-primary-foreground))]">
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.2em] opacity-80 font-semibold">
              Programa de indicação
            </p>
            <h1 className="text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
              Estamos quase lá!
            </h1>
            <p className="text-base lg:text-[17px] leading-relaxed opacity-85">
              Sua inscrição já foi recebida. Em breve você poderá começar a
              divulgar seus links e acompanhar suas indicações.
            </p>
          </div>

          <ul className="space-y-3">
            {bullets.map((b, i) => {
              const Icon = ICON_MAP[b.icon] ?? Sparkles;
              return (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-[14.5px] leading-relaxed opacity-95">
                    {b.text}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="relative z-10 text-xs opacity-70 text-[hsl(var(--aff-primary-foreground))]">
          {tenantName ? `© ${tenantName}` : ''}
        </div>
      </aside>

      {/* CARD — direita */}
      <main className="flex items-center justify-center px-4 py-10 md:p-10 lg:p-14">
        <div className="w-full max-w-md">
          {/* Logo no topo (apenas mobile, já que o hero é hidden md:flex) */}
          {logoUrl && (
            <div className="md:hidden mb-6 flex justify-center">
              <img
                src={logoUrl}
                alt={tenantName ?? ''}
                className="max-h-12 max-w-[200px] object-contain"
              />
            </div>
          )}

          <div className="rounded-2xl border border-border/50 bg-card shadow-xl p-8 text-center space-y-6">
            <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center bg-[hsl(var(--aff-primary)/0.12)]">
              <Clock className="h-8 w-8 text-[hsl(var(--aff-primary))]" />
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-bold tracking-tight text-foreground">
                Aguardando aprovação
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {firstName ? `Olá, ${firstName}! ` : ''}
                Sua conta está sendo analisada
                {tenantName ? ` pela equipe da ${tenantName}` : ' pelo administrador do programa'}.
                Você receberá uma notificação assim que for aprovado.
              </p>
            </div>

            <div className="space-y-3 pt-2">
              <Button
                onClick={() => refresh()}
                variant="outline"
                className="w-full"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Verificar status
              </Button>

              <Button
                onClick={() => signOut()}
                variant="ghost"
                className="w-full"
                asChild
              >
                <Link to={loginPath}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sair
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
